import Modal from "components/DesignSystem/Modal/Modal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useLazyGetSmartReviewQuery } from "store/apis/transactions";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { SmartReviewDateStep } from "./SmartReviewDateStep";
import { SmartReviewNoHistory } from "./SmartReviewNoHistory";
import { SmartReviewResults } from "./SmartReviewResults";

type Step = "date" | "results" | "no_history";

type DateStepValues = {
  start_date: string;
  end_date: string;
};

export const SmartReview = ({ isOpen, close }: ModalProps) => {
  const [step, setStep] = useState<Step>("date");
  const [dateParams, setDateParams] = useState<DateStepValues | null>(null);

  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast } = useToast();

  const [fetchSmartReview, { isLoading }] = useLazyGetSmartReviewQuery();

  const handleDateSubmit = async (values: DateStepValues) => {
    try {
      const result = await fetchSmartReview({
        groupId,
        entityId,
        ...values,
      }).unwrap();

      const hasData =
        (result.default?.total_count ?? 0) > 0 ||
        (result.unmatched_vendor?.total_count ?? 0) > 0 ||
        (result.uncategorised?.total_count ?? 0) > 0 ||
        (result.bank_transfer?.bank_transfer_groups?.length ?? 0) > 0;

      if (!hasData) {
        setStep("no_history");
        return;
      }

      setDateParams(values);
      setStep("results");
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleClose = () => {
    close();
    setStep("date");
    setDateParams(null);
  };

  const isResultsStep = step === "results" && !!dateParams;

  return (
    <>
      <Modal.Root
        open={isOpen && step !== "no_history"}
        onOpenChange={handleClose}
      >
        <Modal.Content
          size={isResultsStep ? "fullscreen" : "regular"}
          useCustomOverlay
        >
          <Modal.Header className="sm:t-pl-4">
            <Modal.Title>Smart review</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          {step === "date" && (
            <SmartReviewDateStep
              isLoading={isLoading}
              onClose={handleClose}
              onSubmit={handleDateSubmit}
            />
          )}
          {isResultsStep && (
            <SmartReviewResults dateParams={dateParams} onClose={handleClose} />
          )}
        </Modal.Content>
      </Modal.Root>

      <SmartReviewNoHistory
        isOpen={isOpen && step === "no_history"}
        onClose={handleClose}
      />
    </>
  );
};
